import styled, { css } from 'styled-components';
import { Modal } from 'src/components';
import { colorSchemePreserveAttrs } from 'src/utils/colorScheme';
import { memMarkdownContent } from '../MemMarkdown/styles';

export const WideModal = styled(Modal)(
  ({ theme }) => css`
    .reactModalContent {
      max-width: 80vw;
      width: 80vw;

      @media (max-width: ${theme.sz.md}) {
        max-width: calc(100vw - ${theme.sz.s4});
        width: calc(100vw - ${theme.sz.s4});
      }
    }
  `,
);

export const ButtonCard = styled.button(
  ({ theme }) => css`
    background-color: ${theme.co.primary99};
    border: 2px dashed ${theme.co.primary80};
    border-radius: ${theme.sz.s2};
    padding: ${theme.sz.s3} ${theme.sz.s2};
    width: fit-content;
    flex-shrink: 0;
    align-self: stretch;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${theme.sz.s2};
    cursor: pointer;
    color: ${theme.co.primary30};
    transition: background-color 0.15s;

    &:hover {
      background-color: ${theme.co.primary95};
    }
  `,
);

export const PlusIcon = styled.span(
  ({ theme }) => css`
    font-size: 24px;
    font-weight: ${theme.DEPRECATED_ty.bold};
    line-height: 1;
  `,
);

export const ButtonLabel = styled.span(
  ({ theme }) => css`
    ${theme.typography.paragraph.s};
    font-weight: ${theme.DEPRECATED_ty.bold};
    text-align: center;
    max-width: 5rem;
  `,
);

export const FormModal = styled.div(
  ({ theme }) => css`
    padding: ${theme.sz.s6};
    box-sizing: border-box;
  `,
);

export const ModalTitle = styled.h2(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.s};
    color: ${theme.co.neutral10};
    margin: 0 0 ${theme.sz.s4} 0;
    padding-right: ${theme.sz.s8};
  `,
);

export const LearnablePreview = styled.div(
  ({ theme }) => css`
    background-color: ${theme.co.neutral95};
    border-radius: ${theme.sz.s1};
    padding: ${theme.sz.s2} ${theme.sz.s3};
    margin-bottom: ${theme.sz.s3};
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s1};
  `,
);

export const LearnableItem = styled.span(
  ({ theme }) => css`
    ${theme.typography.paragraph.base};
    font-weight: ${theme.DEPRECATED_ty.bold};
    color: ${theme.co.neutral10};
  `,
);

export const LearnableDefinition = styled.span(
  ({ theme }) => css`
    ${theme.typography.paragraph.s};
    color: ${theme.co.neutral40};
  `,
);

export const TextArea = styled.textarea(
  ({ theme }) => css`
    ${theme.typography.paragraph.base};
    width: 100%;
    max-width: none;
    border: 1px solid ${theme.co.neutral80};
    border-radius: ${theme.sz.s1};
    padding: ${theme.sz.s2};
    resize: vertical;
    min-height: 80px;
    color: ${theme.co.neutral10};
    background-color: ${theme.co.neutral99};
    box-sizing: border-box;

    &:focus {
      outline: none;
      border-color: ${theme.co.primary50};
    }
  `,
);

export const AiPromptTextArea = styled(TextArea)(
  ({ theme }) => css`
    min-height: calc(1.4em * 5 + ${theme.sz.s4});
  `,
);

export const AiGenerateRow = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s2};
    align-items: stretch;
  `,
);

export const AiError = styled.p(
  ({ theme }) => css`
    ${theme.typography.paragraph.s};
    color: ${theme.co.error40};
    margin: 0;
  `,
);

export const TextPreview = styled.div(
  ({ theme }) => css`
    ${theme.typography.paragraph.base};
    ${memMarkdownContent(theme)};
    padding: ${theme.sz.s2};
    margin-top: ${theme.sz.s1};
    background-color: ${theme.co.neutral95};
    border: 1px solid ${theme.co.neutral80};
    border-radius: ${theme.sz.s1};
    color: ${theme.co.neutral20};
    min-height: 24px;
    line-height: 1.4;
  `,
);

export const CharCount = styled.span<{ $overLimit: boolean }>(
  ({ theme, $overLimit }) => css`
    ${theme.typography.paragraph.s};
    color: ${$overLimit ? theme.co.error40 : theme.co.neutral40};
    text-align: left;
    display: block;
    max-width: none;
    margin-top: ${theme.sz.s1};
  `,
);

export const FormatToolbar = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.sz.s1};
    flex-wrap: wrap;
    margin-bottom: ${theme.sz.s2};
  `,
);

export const FormatButton = styled.button<{ $active?: boolean }>(
  ({ theme, $active }) => css`
    ${theme.typography.paragraph.s};
    background: ${$active ? theme.co.primary95 : 'none'};
    border: 1px solid ${$active ? theme.co.primary70 : theme.co.neutral80};
    border-radius: ${theme.sz.s1};
    padding: ${theme.sz.s1};
    cursor: pointer;
    color: ${$active ? theme.co.primary30 : theme.co.neutral30};
    font-size: 0.875rem;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: ${theme.sz.s5};
    min-height: ${theme.sz.s5};

    &:hover {
      border-color: ${theme.co.primary70};
      color: ${theme.co.primary30};
    }
  `,
);

export const FormatDivider = styled.span(
  ({ theme }) => css`
    width: 1px;
    height: ${theme.sz.s4};
    background: ${theme.co.neutral80};
    margin: 0 ${theme.sz.s1};
    flex-shrink: 0;
  `,
);

export const ToggleChevron = styled.span<{ $open: boolean }>(
  ({ theme, $open }) => css`
    display: inline-block;
    transition: transform 0.2s;
    transform: rotate(${$open ? '180deg' : '0deg'});
    font-size: 1.2rem;
    line-height: 1;
    margin-left: ${theme.sz.s2};
  `,
);

export const ImageSection = styled.div(
  ({ theme }) => css`
    margin-top: ${theme.sz.s3};
    background-color: ${theme.co.neutral95};
    border: 1px solid ${theme.co.neutral80};
    border-radius: ${theme.sz.s2};
    overflow: hidden;
  `,
);

export const ImageSectionToggle = styled.button(
  ({ theme }) => css`
    ${theme.typography.paragraph.base};
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.sz.s2};
    padding: ${theme.sz.s3} ${theme.sz.s4};
    border: none;
    background: transparent;
    color: ${theme.co.neutral10};
    font-weight: ${theme.DEPRECATED_ty.bold};
    cursor: pointer;
    text-align: left;
    box-sizing: border-box;

    &:hover {
      background-color: ${theme.co.neutral90};
    }
  `,
);

export const ImageSectionGrid = styled.div<{ $open: boolean }>(
  ({ $open }) => css`
    display: grid;
    grid-template-rows: ${$open ? '1fr' : '0fr'};
    transition: grid-template-rows 0.3s ease;
    overflow: hidden;
  `,
);

export const ImageSectionInner = styled.div<{ $open: boolean }>(
  ({ theme, $open }) => css`
    overflow: hidden;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s3};
    padding: ${$open ? `${theme.sz.s3} ${theme.sz.s4} ${theme.sz.s4}` : '0'};
  `,
);

export const ImageSourceColumns = styled.div(
  ({ theme }) => css`
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    gap: ${theme.sz.s3};
    align-items: stretch;
    min-height: 220px;

    @media (max-width: ${theme.sz.lg}) {
      grid-template-columns: 1fr;
      grid-template-rows: auto auto auto;
      min-height: 0;
    }
  `,
);

export const ImageSourcePanel = styled.div<{ $active: boolean }>(
  ({ theme, $active }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s3};
    padding: ${theme.sz.s3};
    border-radius: ${theme.sz.s2};
    border: 2px solid ${$active ? theme.co.primary70 : theme.co.neutral80};
    background-color: ${$active ? theme.co.neutral99 : theme.co.neutral90};
    opacity: ${$active ? 1 : 0.55};
    cursor: pointer;
    transition:
      opacity 0.2s,
      border-color 0.2s,
      background-color 0.2s;
    box-sizing: border-box;
    min-width: 0;

    &:hover {
      border-color: ${$active ? theme.co.primary50 : theme.co.neutral60};
    }
  `,
);

export const ImageSourceTitle = styled.h3(
  ({ theme }) => css`
    ${theme.typography.paragraph.base};
    font-weight: ${theme.DEPRECATED_ty.bold};
    color: ${theme.co.neutral10};
    margin: 0;
  `,
);

export const ImageSourceBody = styled.div<{ $active: boolean }>(
  ({ theme, $active }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s3};
    flex: 1;
    min-height: 0;
    pointer-events: ${$active ? 'auto' : 'none'};
  `,
);

export const ImageSourceDivider = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    padding-top: ${theme.sz.s1};
    gap: ${theme.sz.s2};
    position: relative;

    @media (max-width: ${theme.sz.lg}) {
      flex-direction: row;
      justify-content: center;
      padding: ${theme.sz.s1} 0;
    }
  `,
);

export const ImageSourceDividerLine = styled.div(
  ({ theme }) => css`
    width: 1px;
    flex: 1;
    min-height: ${theme.sz.s8};
    background-color: ${theme.co.neutral70};

    @media (max-width: ${theme.sz.lg}) {
      width: auto;
      flex: 1;
      min-height: 1px;
      height: 1px;
    }
  `,
);

export const ImageSourceOr = styled.span(
  ({ theme }) => css`
    ${theme.typography.paragraph.s};
    color: ${theme.co.neutral40};
    font-weight: ${theme.DEPRECATED_ty.bold};
    text-transform: lowercase;
    background-color: ${theme.co.neutral95};
    padding: ${theme.sz.s1};
    line-height: 1;
  `,
);

export const ImageUploadArea = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${theme.sz.s1};
    border: 2px dashed ${theme.co.neutral80};
    border-radius: ${theme.sz.s2};
    padding: ${theme.sz.s4};
    cursor: pointer;
    min-height: 80px;
    ${theme.typography.paragraph.s};
    color: ${theme.co.neutral40};
    transition:
      border-color 0.15s,
      color 0.15s;

    &:hover {
      border-color: ${theme.co.primary50};
      color: ${theme.co.primary40};
    }
  `,
);

/* ── Image preview + contextual controls ─────────────────────────────── */

export const ImageControlsWrapper = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s2};
    margin-top: ${theme.sz.s2};
  `,
);

export const ImageRow = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: stretch;
    gap: ${theme.sz.s2};
  `,
);

export const ImagePreviewCard = styled.div.attrs(colorSchemePreserveAttrs)<{
  $imageUrl: string;
  $imageX: number;
  $imageY: number;
  $isDragging: boolean;
  $aspectRatio?: string | null;
  $isCropped?: boolean;
}>(
  ({ theme, $imageUrl, $imageX, $imageY, $isDragging, $aspectRatio, $isCropped }) => css`
    position: relative;
    overflow: hidden;
    border-radius: ${theme.sz.s2};
    background-image: url('${$imageUrl}');
    background-size: ${$isCropped ? 'contain' : 'cover'};
    background-position: ${$isCropped ? 'center' : `${$imageX}% ${$imageY}%`};
    background-repeat: no-repeat;
    display: flex;
    flex-direction: column;
    padding: ${theme.sz.s3};
    box-sizing: border-box;
    cursor: ${$isDragging ? 'grabbing' : 'grab'};
    user-select: none;

    /* Use same intelligent sizing as edit modal */
    ${
      $aspectRatio
        ? css`
            aspect-ratio: ${$aspectRatio};
            min-width: 200px;
            max-width: 400px;
            min-height: 150px;
            max-height: 500px;
          `
        : css`
            /* Fallback when aspect ratio isn't available */
            width: 240px;
            height: 300px;
          `
    }
  `,
);

export const DragHint = styled.span(
  ({ theme }) => css`
    position: absolute;
    top: ${theme.sz.s2};
    right: ${theme.sz.s2};
    ${theme.typography.paragraph.s};
    font-size: 0.65rem;
    color: rgba(255, 255, 255, 0.75);
    background: rgba(0, 0, 0, 0.35);
    border-radius: ${theme.sz.s1};
    padding: 2px ${theme.sz.s1};
    pointer-events: none;
    z-index: 2;
  `,
);

export const ImagePreviewText = styled.div<{
  $align?: string;
  $position?: string;
  $fontSize?: number;
}>(
  ({ theme, $align, $position, $fontSize }) => css`
    ${theme.typography.paragraph.s};
    ${memMarkdownContent(theme)};
    font-size: ${$fontSize ?? 1}rem;
    color: ${theme.co.neutral99};
    margin: 0;
    position: relative;
    z-index: 1;
    text-align: ${$align ?? 'left'};
    text-shadow:
      -1px -1px 0 rgba(0, 0, 0, 0.9),
      1px -1px 0 rgba(0, 0, 0, 0.9),
      -1px 1px 0 rgba(0, 0, 0, 0.9),
      1px 1px 0 rgba(0, 0, 0, 0.9);
    ${
      $position === 'top' &&
      css`
        margin-bottom: auto;
      `
    }
    ${
      (!$position || $position === 'bottom') &&
      css`
        margin-top: auto;
      `
    }
    ${
      $position === 'middle' &&
      css`
        margin-top: auto;
        margin-bottom: auto;
      `
    }
  `,
);

export const VerticalButtons = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: ${theme.sz.s1};
    flex-shrink: 0;
  `,
);

export const HorizontalControls = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.sz.s2};
    flex-wrap: wrap;
  `,
);

export const ControlGroup = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.sz.s1};
  `,
);

export const FontSizeLabel = styled.span(
  ({ theme }) => css`
    ${theme.typography.paragraph.s};
    font-size: 0.75rem;
    color: ${theme.co.neutral40};
    min-width: ${theme.sz.s3};
    text-align: center;
    user-select: none;
  `,
);

export const ImageActionButton = styled.button(
  ({ theme }) => css`
    ${theme.typography.paragraph.s};
    font-size: 0.75rem;
    background: none;
    border: 1px solid ${theme.co.neutral80};
    border-radius: ${theme.sz.s1};
    padding: 2px ${theme.sz.s2};
    cursor: pointer;
    color: ${theme.co.neutral40};

    &:hover {
      border-color: ${theme.co.neutral60};
      color: ${theme.co.neutral20};
    }
  `,
);

/* ── Crop mode ───────────────────────────────────────────────────────── */

export const CropContainer = styled.div(
  ({ theme }) => css`
    position: relative;
    cursor: crosshair;
    overflow: hidden;
    border-radius: ${theme.sz.s2};
    user-select: none;
    line-height: 0;
  `,
);

export const CropSourceImg = styled.img`
  width: 100%;
  height: auto;
  display: block;
  max-height: 350px;
  object-fit: contain;
  -webkit-user-drag: none;
`;

export const CropSelection = styled.div<{ $x: number; $y: number; $w: number; $h: number }>(
  ({ $x, $y, $w, $h }) => css`
    position: absolute;
    left: ${$x}px;
    top: ${$y}px;
    width: ${$w}px;
    height: ${$h}px;
    border: 2px solid white;
    box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5);
    box-sizing: border-box;
    pointer-events: none;
  `,
);

export const CropActionRow = styled.div(
  ({ theme }) => css`
    display: flex;
    justify-content: flex-end;
    gap: ${theme.sz.s2};
    margin-top: ${theme.sz.s2};
  `,
);

export const CropActionButton = styled.button<{ $primary?: boolean }>(
  ({ theme, $primary }) => css`
    ${theme.typography.paragraph.s};
    background: ${$primary ? theme.co.primary50 : 'none'};
    color: ${$primary ? theme.co.neutral99 : theme.co.neutral30};
    border: 1px solid ${$primary ? theme.co.primary50 : theme.co.neutral80};
    border-radius: ${theme.sz.s1};
    padding: ${theme.sz.s1} ${theme.sz.s3};
    cursor: pointer;

    &:hover:not(:disabled) {
      background: ${$primary ? theme.co.primary40 : theme.co.neutral95};
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  `,
);

/* ── Shared bottom ───────────────────────────────────────────────────── */

export const SubmitError = styled.p(
  ({ theme }) => css`
    ${theme.typography.paragraph.s};
    color: ${theme.co.error40};
    text-align: right;
    margin: ${theme.sz.s2} 0 0;
    max-width: none;
  `,
);

export const ModalButtons = styled.div(
  ({ theme }) => css`
    display: flex;
    gap: ${theme.sz.s2};
    justify-content: flex-end;
    margin-top: ${theme.sz.s4};
  `,
);
